import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { DialogQueryState } from "@/components/shared/DialogQueryState";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAgency } from "@/hooks/use-agency";
import { apiGet } from "@/lib/api-client";
import { agencyKeys } from "@/lib/query";
import { ScorecardAgentsSchema } from "@/schemas/agent";

interface ScorecardGroupDialogProps {
  agentNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ScorecardGroupDialog({
  agentNumber,
  open,
  onOpenChange,
}: ScorecardGroupDialogProps) {
  const { data: agencyData, isLoading: isAgencyLoading } = useAgency(agentNumber);
  // useAgency now returns AgencyDetail (validated by Zod) — no cast needed.
  const group = agencyData?.agent?.agentScorecardGroup ?? "";

  const {
    data: agents,
    isLoading: isGroupLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: agencyKeys.scorecardGroups(group),
    queryFn: () =>
      apiGet(`/api/agencies/scorecard/${encodeURIComponent(group)}`, ScorecardAgentsSchema),
    enabled: open && group.length > 0,
  });

  const isLoading = isAgencyLoading || isGroupLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Scorecard Group</DialogTitle>
          <DialogDescription>
            {group ? `Group ${group}` : "No scorecard group assigned"} — Agent {agentNumber}
          </DialogDescription>
        </DialogHeader>

        <DialogQueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={() => refetch()}
          errorMessage="Failed to load scorecard group"
          isEmpty={
            !isLoading && !isError && (!group || (agents !== undefined && agents.length === 0))
          }
          emptyIcon={Users}
          emptyMessage={
            !group
              ? "This agency does not have a scorecard group assigned."
              : `No agents found in scorecard group "${group}".`
          }
          skeletonHeight="h-10"
        />

        {agents && agents.length > 0 && (
          <div className="max-h-[60vh] overflow-x-auto overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-2 py-2 font-medium">Agent Number</th>
                  <th className="px-2 py-2 font-medium">Agent Name</th>
                  <th className="px-2 py-2 font-medium">Tier</th>
                  <th className="px-2 py-2 font-medium">Contracted</th>
                  <th className="px-2 py-2 font-medium">Scorecard Group</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b transition-colors last:border-0 hover:bg-muted/30"
                  >
                    <td className="px-2 py-2 font-medium">
                      <Link
                        to="/agency/$agentNumber"
                        params={{ agentNumber: a.agentNumber }}
                        className="text-primary underline-offset-4 hover:underline"
                        onClick={() => onOpenChange(false)}
                      >
                        {a.agentNumber}
                      </Link>
                    </td>
                    <td className="px-2 py-2">{a.agentName}</td>
                    <td className="px-2 py-2">{a.agentTier ?? "—"}</td>
                    <td className="px-2 py-2">
                      <StatusBadge
                        statusKey={a.agentContractedYn === "Y" ? "policy-active" : "policy-expired"}
                        label={a.agentContractedYn === "Y" ? "Yes" : "No"}
                      />
                    </td>
                    <td className="px-2 py-2">{a.agentScorecardGroup ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-muted-foreground text-xs">
              Showing {agents.length} agent{agents.length !== 1 ? "s" : ""}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
